import {
  fetchMyDashboardLayout,
  saveMyDashboardLayout as saveMyDashboardLayoutApi,
  clearMyDashboardLayout as clearMyDashboardLayoutApi,
} from "../../api/User/myDashboardApi";

const STORAGE_PREFIX = "matrix.myDashboard.";

export function getDashboardStorageKey(userId) {
  return `${STORAGE_PREFIX}${userId || "anonymous"}`;
}

function isValidLayout(parsed) {
  return (
    parsed &&
    Array.isArray(parsed.widgets) &&
    parsed.layouts &&
    typeof parsed.layouts === "object"
  );
}

/** Cache local (rápido / offline). */
export function loadDashboardLayoutLocal(userId) {
  try {
    const raw = localStorage.getItem(getDashboardStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidLayout(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveDashboardLayoutLocal(userId, layout) {
  const payload = {
    version: layout?.version ?? 1,
    updatedAt: new Date().toISOString(),
    widgets: layout?.widgets || [],
    layouts: layout?.layouts || { lg: [] },
  };
  localStorage.setItem(getDashboardStorageKey(userId), JSON.stringify(payload));
  return payload;
}

export function clearDashboardLayoutLocal(userId) {
  localStorage.removeItem(getDashboardStorageKey(userId));
}

/**
 * Carga layout: backend primero; si falla o vacío, cache local.
 * Si backend vacío pero hay cache local, migra el cache al backend.
 */
export async function loadDashboardLayout(userId) {
  try {
    const remote = await fetchMyDashboardLayout();
    if (isValidLayout(remote)) {
      saveDashboardLayoutLocal(userId, remote);
      return remote;
    }

    const local = loadDashboardLayoutLocal(userId);
    if (isValidLayout(local)) {
      try {
        const saved = await saveMyDashboardLayoutApi(local);
        if (isValidLayout(saved)) {
          saveDashboardLayoutLocal(userId, saved);
          return saved;
        }
      } catch (err) {
        console.warn("No se pudo migrar dashboard local → backend:", err);
      }
      return local;
    }
    return null;
  } catch (err) {
    console.warn("Dashboard layout backend no disponible, uso local:", err);
    return loadDashboardLayoutLocal(userId);
  }
}

/** Guarda en backend y cache local. */
export async function saveDashboardLayout(userId, layout) {
  const payload = {
    version: 1,
    updatedAt: new Date().toISOString(),
    widgets: layout?.widgets || [],
    layouts: layout?.layouts || { lg: [] },
  };

  saveDashboardLayoutLocal(userId, payload);

  try {
    const saved = await saveMyDashboardLayoutApi(payload);
    if (isValidLayout(saved)) {
      saveDashboardLayoutLocal(userId, saved);
      return saved;
    }
  } catch (err) {
    console.error("Error guardando dashboard en backend:", err);
    throw err;
  }
  return payload;
}

/** Borra backend + cache local. */
export async function clearDashboardLayout(userId) {
  clearDashboardLayoutLocal(userId);
  try {
    await clearMyDashboardLayoutApi();
  } catch (err) {
    console.error("Error limpiando dashboard en backend:", err);
    throw err;
  }
}

// Compat aliases usados antes
export const loadDashboardLayoutSync = loadDashboardLayoutLocal;
export const saveDashboardLayoutSync = saveDashboardLayoutLocal;
